import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Phone, MapPin } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { ProjectCard } from "@/components/home/Projects";
import { getArea, nearbyAreas, REGIONS } from "@/data/areas";
import { PROJECTS, projectFor } from "@/data/projects";
import { SERVICES } from "@/data/services";
import { SITE } from "@/lib/site";
import NotFound from "./NotFound";

const REGION_IMG = { "st-george": "/images/projects/hurstville/master-bath.jpg", "sutherland-shire": "/images/ig/p10_13.jpg", "canterbury-bankstown": "/images/ig/p10_8.jpg", "inner-east": "/images/projects/connells-point/kitchen.jpg", "north": "/images/ig/p10_3.jpg", "western-sydney": "/images/projects/beverly-park/kitchen.jpg", "macarthur": "/images/projects/beverly-park/facade.jpg" };

const localProjects = (a) => {
  const own = projectFor(a.name);
  const near = nearbyAreas(a, 30).map((n) => projectFor(n.name)).filter(Boolean);
  return [...new Set([own, ...near, ...PROJECTS].filter(Boolean))].filter((p) => p.id !== "panania" && p.id !== "details").slice(0, 3);
};

export default function AreaDetail() {
  const { slug } = useParams();
  const a = getArea(slug);
  if (!a) return <NotFound />;
  const r = REGIONS[a.region];
  const own = projectFor(a.name);
  const jsonLd = { "@context": "https://schema.org", "@type": "Plumber", name: "Austyle Plumbing", telephone: "0452 538 075", email: SITE.email,
    areaServed: { "@type": "Place", name: `${a.name} NSW ${a.postcode}` }, sameAs: [SITE.instagram] };
  return (
    <>
      <Seo title={`Plumber ${a.name} NSW ${a.postcode} | Austyle Plumbing`}
        description={`Local plumber in ${a.name} NSW ${a.postcode}. Austyle Plumbing – 30 years of domestic, commercial, strata and new build plumbing across ${r.name}. Book online.`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>
      <PageHero testid="area-hero" eyebrow={`${r.name} · NSW ${a.postcode}`} lines={["Plumber", `${a.name}.`]}
        image={(own?.id !== "panania" && own?.image) || REGION_IMG[a.region]} intro={`Reliable domestic, commercial and industrial plumbing for ${a.name} and surrounding suburbs — from new builds and strata to everyday repairs.`} />
      <section className="bg-porcelain py-20 sm:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow text-cobalt mb-5">Plumbing in {a.name}</p></Reveal>
            <Reveal><p data-testid="area-intro" className="font-wide font-bold text-2xl sm:text-3xl leading-snug tracking-tight">
              Looking for a plumber in {a.name}? Austyle Plumbing has {SITE.years} years in the trade and works right across {r.name}.
            </p></Reveal>
            <Reveal><p className="mt-6 text-slate-700 leading-relaxed">{r.blurb}</p></Reveal>
            {own && <Reveal><p className="mt-4 text-slate-700 leading-relaxed">Recently, our team completed the plumbing on the <b>{own.title.toLowerCase()}</b> in {a.name}. {own.note}</p></Reveal>}
            <h2 className="mt-14 font-wide font-black uppercase text-3xl tracking-tight"><MaskLines lines={[`Services in ${a.name}`]} /></h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s.slug} className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)]">
                  <Link to={`/services/${s.slug}`} data-testid={`area-service-${s.slug}`} className="group flex items-center justify-between p-5 hover:bg-porcelain transition-colors">
                    <span><span className="font-bold">{s.title}</span><span className="block text-xs text-slate-500 mt-0.5">{s.tag} · {a.name}</span></span>
                    <ArrowUpRight className="h-4 w-4 text-cobalt transition-transform group-hover:rotate-45" />
                  </Link>
                </li>
              ))}
              <li className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)]">
                <a href={SITE.tel} data-testid="area-service-other" className="flex items-center justify-between p-5 hover:bg-porcelain transition-colors">
                  <span><span className="font-bold">Something else?</span><span className="block text-xs text-slate-500 mt-0.5">Call {SITE.phone}</span></span>
                  <Phone className="h-4 w-4 text-cobalt" />
                </a>
              </li>
            </ul>
          </div>
          <aside className="lg:col-span-5 space-y-4">
            <div className="lg:sticky lg:top-28 space-y-4">
              <div className="bg-ink text-white p-8 sm:p-10 clip-notch">
                <p className="eyebrow text-brass">Plumber near {a.name}</p>
                <p className="font-wide font-black text-3xl mt-4 leading-tight">Book a time or get a quote in minutes.</p>
                <Link to={`/book?suburb=${encodeURIComponent(a.name)}`} data-testid="area-book-button" className="group mt-8 flex items-center justify-between bg-brass text-ink font-bold h-14 px-6 rounded-full hover:bg-white transition-colors">
                  Book in {a.name} <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={SITE.tel} data-testid="area-call-button" className="mt-3 flex items-center justify-between border border-white/25 h-14 px-6 rounded-full font-mono hover:border-brass hover:text-brass transition-colors">
                  <span className="flex items-center gap-2"><Phone className="h-4 w-4" />Call</span>{SITE.phone}
                </a>
              </div>
              <div className="bg-white border border-ink/10 p-6">
                <p className="eyebrow text-cobalt mb-4">Nearby suburbs</p>
                <div className="flex flex-wrap gap-2">
                  {nearbyAreas(a).map((n) => (
                    <Link key={n.slug} to={`/areas/${n.slug}`} data-testid={`nearby-${n.slug}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-ink/15 text-sm font-semibold hover:bg-ink hover:text-white transition-colors">
                      <MapPin className="h-3.5 w-3.5" />{n.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <section className="bg-bone py-20 sm:py-24">
        <div className="container-x">
          <p className="eyebrow text-cobalt mb-8">Recent work near {a.name}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {localProjects(a).map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
          </div>
        </div>
      </section>
      <CtaBand title={[`Need a plumber`, `in ${a.name}?`]} />
    </>
  );
}
